import T, { useTx } from './T';
import React, {useState} from 'react';
import {
  TextInput as RNTextInput,
  TextInputProps as RNTextInputProps,
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import {
  useController,
  UseControllerProps,
  useFormContext,
} from 'react-hook-form';
import { recordPick, splitTrailingWord, suggest, transliterateTrailing } from '../utils/transliterate';
import { usePhoneticLang } from '../utils/transliterate/usePhoneticInput';

interface TextInputProps extends RNTextInputProps, UseControllerProps {
  label: string;
  defaultValue?: string;
  /** false => never transliterate this field */
  phonetic?: boolean;
}

const ControlledInput = (props: TextInputProps) => {
  const formContext = useFormContext();
  const {formState} = formContext;

  const {name, label, rules, defaultValue, onChangeText, phonetic, ...inputProps} = props;
  const tx = useTx();
  const phoneticLang = usePhoneticLang();
  const phoneticOn =
    !!phoneticLang &&
    phonetic !== false &&
    !inputProps.secureTextEntry &&
    (!inputProps.keyboardType || inputProps.keyboardType === 'default');

  const {field, fieldState} = useController({name, rules, defaultValue});
  const [caret, setCaret] = useState<number | null>(null);
  const [focused, setFocused] = useState(false);

  // Suggestion chips for the Latin word before the caret.
  const curValue = field.value?.toString() ?? '';
  const parts =
    phoneticOn && focused ? splitTrailingWord(curValue, caret ?? curValue.length) : null;
  const chips = parts ? suggest(parts.word, phoneticLang as string) : [];

  const pick = (cand: string) => {
    if (!parts) return;
    recordPick(phoneticLang as string, parts.word, cand);
    const text = parts.head + cand + (parts.tail.startsWith(' ') ? '' : ' ') + parts.tail;
    field.onChange(text);
    if (onChangeText) onChangeText(text);
    const pos = (parts.head + cand + ' ').length;
    setCaret(pos);
  };

  return (
    <View style={styles.container}>
      {label && <Text style={styles.label}><T>{label}</T></Text>}
      <View style={styles.input}>
        <RNTextInput
          style={[styles.input, !!fieldState.error && styles.inputError]}
          onBlur={() => {
            if (phoneticOn) {
              const cur = field.value?.toString() ?? '';
              const flushed = transliterateTrailing(cur, phoneticLang as string, true);
              if (flushed !== cur) {
                field.onChange(flushed);
                if (onChangeText) onChangeText(flushed);
              }
            }
            setFocused(false);
            field.onBlur();
          }}
          onFocus={() => setFocused(true)}
          onSelectionChange={(e) => setCaret(e.nativeEvent.selection.start)}
          value={field.value?.toString()}
          onChangeText={(text: string) => {
            if (phoneticOn) {
              text = transliterateTrailing(text, phoneticLang as string);
            }
            field.onChange(text);
            if (onChangeText) {
              onChangeText(text);
            }
          }}
          autoComplete="off" // ✅ Disable autocomplete (Android + iOS 14+)
          autoCorrect={false} // ✅ Prevent autocorrection
          importantForAutofill="no" // ✅ Android only
          textContentType="none" // ✅ iOS only (disables autofill context)
          selectTextOnFocus={inputProps.selectTextOnFocus ?? false}
          {...inputProps}
          placeholder={typeof inputProps.placeholder === 'string' ? tx(inputProps.placeholder) : inputProps.placeholder}
        />
        {fieldState.error && (
          <Text style={styles.error}><T>{fieldState.error.message}</T></Text>
        )}
      </View>
      {chips.length > 0 && (
        <ScrollView
          horizontal
          keyboardShouldPersistTaps="always"
          showsHorizontalScrollIndicator={false}
          style={styles.chipStrip}>
          {chips.map((c, i) => (
            <TouchableOpacity key={c + i} style={styles.chip} onPress={() => pick(c)}>
              <Text style={styles.chipText}>{c}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

export const TextInput = (props: TextInputProps) => {
  const {name} = props;

  const formContext = useFormContext();

  if (!formContext || !name) {
    const msg = !formContext
      ? 'TextInput must be wrapped by the FormProvider'
      : 'Name must be defined';
    console.error(msg);
    return null;
  }

  return <ControlledInput {...props} />;
};

const styles = StyleSheet.create({
  label: {
    marginLeft: 0,
  },
  container: {
    flex: -1,
    justifyContent: 'center',
    padding: 0,
  },
  chipStrip: {
    flexGrow: 0,
    marginTop: 2,
  },
  chip: {
    backgroundColor: '#e8f0fe',
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 6,
  },
  chipText: {
    fontSize: 16,
    color: '#1a3d7c',
  },
  error: {
    marginLeft: 0,
    color: 'red',
  },
  input: {
    backgroundColor: 'white',
    height: 50,
    borderRadius: 4,
  },
  inputError: {
    borderColor: 'red',
    backgroundColor: '#f9d7d3',
  },
});

